// MaskCanvas: Canvas for mask drawing in InpaintNode
import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';

interface MaskCanvasProps {
  imageUrl: string;
  brushSize: number;
  tool?: 'brush' | 'eraser';
  onMaskChange: (maskDataUrl: string) => void;
  zoom: number;
}

export interface MaskCanvasHandle {
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

const MaskCanvas = forwardRef<MaskCanvasHandle, MaskCanvasProps>(({ 
  imageUrl, 
  brushSize,
  tool = 'brush',
  onMaskChange,
  zoom
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null); // Separate canvas for mask-only
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [maskCtx, setMaskCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [cursorCtx, setCursorCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [history, setHistory] = useState<ImageData[]>([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const latestCursorPos = useRef<{ x: number; y: number } | null>(null);
  const lastDrawnCursorPos = useRef<{ x: number; y: number } | null>(null);
  const cursorNeedsUpdate = useRef(false);
  const animationFrameId = useRef<number | null>(null);
  const isHovering = useRef(false);

  // Load image
  useEffect(() => {
    const canvas = canvasRef.current;
    const cursorCanvas = cursorCanvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    if (!canvas || !cursorCanvas || !maskCanvas) return;
    
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const cursorContext = cursorCanvas.getContext('2d');
    const maskContext = maskCanvas.getContext('2d', { willReadFrequently: true });
    if (!context || !cursorContext || !maskContext) return;
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      cursorCanvas.width = img.width;
      cursorCanvas.height = img.height;
      maskCanvas.width = img.width;
      maskCanvas.height = img.height;
      
      // Draw image on display canvas
      context.drawImage(img, 0, 0);
      
      // Fill mask canvas with black (no mask)
      maskContext.fillStyle = 'black';
      maskContext.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
      
      setCtx(context);
      setCursorCtx(cursorContext);
      setMaskCtx(maskContext);
      
      // Save initial state to history
      const initialState = context.getImageData(0, 0, canvas.width, canvas.height);
      setHistory([initialState]);
      setHistoryStep(0);
    };
  }, [imageUrl]);

  // Draw cursor circle using animation frame - only when hovering and position changed
  useEffect(() => {
    if (!cursorCtx || !cursorCanvasRef.current) return;
    
    const updateCursor = () => {
      const canvas = cursorCanvasRef.current;
      if (!canvas || !cursorCtx) return;
      
      const pos = latestCursorPos.current;
      const lastPos = lastDrawnCursorPos.current;
      
      // Only redraw if position actually changed or cursor needs update
      const posChanged = !pos !== !lastPos || 
        (pos && lastPos && (pos.x !== lastPos.x || pos.y !== lastPos.y));
      
      if (cursorNeedsUpdate.current || posChanged) {
        cursorCtx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (pos) {
          // Draw circle at cursor position
          cursorCtx.beginPath();
          cursorCtx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
          cursorCtx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
          cursorCtx.lineWidth = 2;
          cursorCtx.stroke();
          
          // Inner circle for better visibility
          cursorCtx.beginPath();
          cursorCtx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
          cursorCtx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
          cursorCtx.lineWidth = 1;
          cursorCtx.stroke();
        }
        
        lastDrawnCursorPos.current = pos ? { ...pos } : null;
        cursorNeedsUpdate.current = false;
      }
      
      // Only continue animation loop while hovering
      if (isHovering.current) {
        animationFrameId.current = requestAnimationFrame(updateCursor);
      }
    };
    
    // Start loop if hovering
    if (isHovering.current) {
      animationFrameId.current = requestAnimationFrame(updateCursor);
    }
    
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [brushSize, cursorCtx]);

  // Save state to history
  const saveState = useCallback(() => {
    if (!ctx || !canvasRef.current || !maskCanvasRef.current) return;
    
    const canvas = canvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Remove any states after current step (when drawing after undo)
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(currentState);
    
    // Limit history to 50 states
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryStep(historyStep + 1);
    }
    
    setHistory(newHistory);
    
    // Export mask-only canvas (black and white)
    const maskDataUrl = maskCanvas.toDataURL();
    onMaskChange(maskDataUrl);
  }, [ctx, history, historyStep, onMaskChange]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyStep > 0 && ctx && canvasRef.current) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      ctx.putImageData(history[newStep], 0, 0);
      
      const maskDataUrl = canvasRef.current.toDataURL();
      onMaskChange(maskDataUrl);
    }
  }, [historyStep, ctx, history, onMaskChange]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyStep < history.length - 1 && ctx && canvasRef.current) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      ctx.putImageData(history[newStep], 0, 0);
      
      const maskDataUrl = canvasRef.current.toDataURL();
      onMaskChange(maskDataUrl);
    }
  }, [historyStep, history, ctx, onMaskChange]);

  // Expose undo/redo to parent via imperative handle
  useImperativeHandle(ref, () => ({
    undo: handleUndo,
    redo: handleRedo,
    canUndo: () => historyStep > 0,
    canRedo: () => historyStep < history.length - 1
  }), [handleUndo, handleRedo, historyStep, history.length]);

  // Get proper canvas coordinates accounting for scale
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    return { x, y };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDrawing(true);
    
    if (!ctx || !maskCtx) return;
    const { x, y } = getCanvasCoordinates(e);
    
    // Draw on display canvas (red overlay for brush, transparent for eraser)
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(255, 0, 0, 1)'; // Will be erased anyway
    } else {
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.globalCompositeOperation = 'source-over';
    }
    
    // Draw on mask canvas (white for brush, black for eraser)
    maskCtx.beginPath();
    maskCtx.moveTo(x, y);
    maskCtx.lineWidth = brushSize;
    maskCtx.lineCap = 'round';
    maskCtx.lineJoin = 'round';
    if (tool === 'eraser') {
      maskCtx.strokeStyle = 'black';
      maskCtx.globalCompositeOperation = 'source-over';
    } else {
      maskCtx.strokeStyle = 'white';
      maskCtx.globalCompositeOperation = 'source-over';
    }
  };

  const stopDrawing = () => {
    if (isDrawing && ctx && maskCtx) {
      setIsDrawing(false);
      ctx.closePath();
      maskCtx.closePath();
      saveState();
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getCanvasCoordinates(e);
    latestCursorPos.current = { x, y };
    
    if (!isDrawing || !ctx || !maskCtx) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    // Draw on display canvas
    ctx.lineTo(x, y);
    ctx.stroke();
    
    // Draw on mask canvas
    maskCtx.lineTo(x, y);
    maskCtx.stroke();
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getCanvasCoordinates(e);
    latestCursorPos.current = { x, y };
    isHovering.current = true;
    cursorNeedsUpdate.current = true;
    // Start animation loop
    if (cursorCtx && !animationFrameId.current) {
      const updateCursor = () => {
        const canvas = cursorCanvasRef.current;
        if (!canvas || !cursorCtx) return;
        
        const pos = latestCursorPos.current;
        const lastPos = lastDrawnCursorPos.current;
        
        const posChanged = !pos !== !lastPos || 
          (pos && lastPos && (pos.x !== lastPos.x || pos.y !== lastPos.y));
        
        if (cursorNeedsUpdate.current || posChanged) {
          cursorCtx.clearRect(0, 0, canvas.width, canvas.height);
          
          if (pos) {
            cursorCtx.beginPath();
            cursorCtx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
            cursorCtx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
            cursorCtx.lineWidth = 2;
            cursorCtx.stroke();
            
            cursorCtx.beginPath();
            cursorCtx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
            cursorCtx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
            cursorCtx.lineWidth = 1;
            cursorCtx.stroke();
          }
          
          lastDrawnCursorPos.current = pos ? { ...pos } : null;
          cursorNeedsUpdate.current = false;
        }
        
        if (isHovering.current) {
          animationFrameId.current = requestAnimationFrame(updateCursor);
        } else {
          animationFrameId.current = null;
        }
      };
      animationFrameId.current = requestAnimationFrame(updateCursor);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.stopPropagation();
    latestCursorPos.current = null;
    isHovering.current = false;
    cursorNeedsUpdate.current = true;
    // Clear cursor canvas immediately
    if (cursorCtx && cursorCanvasRef.current) {
      cursorCtx.clearRect(0, 0, cursorCanvasRef.current.width, cursorCanvasRef.current.height);
    }
    stopDrawing();
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        overflow: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        contain: 'layout style'
      }}
    >
      <div style={{ 
        position: 'relative', 
        display: 'inline-block',
        transform: `scale(${zoom})`,
        transformOrigin: 'center center',
        willChange: 'transform'
      }}>
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseUp={stopDrawing}
          onMouseMove={draw}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ 
            border: '2px solid var(--node-border-color)',
            cursor: 'none',
            maxWidth: '100%',
            height: 'auto',
            imageRendering: 'crisp-edges',
            display: 'block'
          }}
        />
        <canvas
          ref={cursorCanvasRef}
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            display: 'block'
          }}
        />
        <canvas
          ref={maskCanvasRef}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
});

MaskCanvas.displayName = 'MaskCanvas';

export default MaskCanvas;
